/**
 * The prefix section's pure half — the timeline, the pill and the copy, as functions of one
 * `companyPrefixes.list` row, so each state is a value a test can assert without a browser.
 *
 * The timeline's steps are the Onboarding mock's state E, less its review-time figure: the
 * Decision of 2026-09-23 §3c dropped it as a claim with no observer.
 */
import {
  permitsPublishing,
  type CompanyPrefixSummary,
  type Verification,
} from "@packagepage/shared";
import type { TimelineStep } from "@packagepage/ui";

import { relativeTime } from "./domain-setup.js";
import { resolveTimeline } from "./domain-timeline.js";

/** Where the dashboard sends a tenant to claim, upload and wait. */
export const PREFIX_SCREEN_PATH = "/settings/organisation";

/** What a tenant reads in place of a review time — the house voice of `Domains.tsx`. */
export const REVIEW_ANSWER_NOTE =
  "The answer appears on this screen. We cannot email you when it does, because this " +
  "product has no mail transport yet.";

export const PREFIX_TIMELINE_STEPS = [
  { id: "received", label: "Licence received" },
  { id: "review", label: "A person is reading it" },
  { id: "verified", label: "Verified — publishing opens" },
] as const;

type PrefixStepId = (typeof PREFIX_TIMELINE_STEPS)[number]["id"];

/** Index of the step in progress. Past the last step when verified; `disputed` has no timeline. */
export function prefixStepIndex(verification: Verification): number {
  if (permitsPublishing(verification)) return PREFIX_TIMELINE_STEPS.length;
  if (verification === "pending") return 1;
  return 0;
}

export function prefixTimeline(
  prefix: CompanyPrefixSummary,
  now: Date,
): readonly TimelineStep[] {
  const latest = prefix.documents.at(-1);
  const details: Partial<Record<PrefixStepId, string>> = {
    received:
      latest === undefined
        ? "Upload your GS1 licence certificate below."
        : `Received ${relativeTime(latest.submitted_at, now)}${
            latest.filename === null ? "" : ` · ${latest.filename}`
          }`,
    review: REVIEW_ANSWER_NOTE,
  };
  if (prefix.verified_at !== null) {
    details.verified = `Verified ${relativeTime(prefix.verified_at, now)}.`;
  }
  return resolveTimeline(
    PREFIX_TIMELINE_STEPS,
    prefixStepIndex(prefix.verification),
    details,
  );
}

/** `packages/ui`'s prefix pill family — `verified_*` collapse to one value, as the mock draws it. */
export function prefixPill(
  verification: Verification,
): "unverified" | "pending" | "verified" | "disputed" {
  if (permitsPublishing(verification)) return "verified";
  if (verification === "pending" || verification === "disputed") return verification;
  return "unverified";
}

export const PREFIX_STATE_LABELS: Record<Verification, string> = {
  unverified: "Unverified",
  pending: "Awaiting review",
  verified_document: "Verified · document",
  verified_registry: "Verified · registry",
  disputed: "Disputed",
};

/** Whether the row offers an upload, and what the control says. `null` once verified. */
export function uploadPrompt(verification: Verification): string | null {
  if (permitsPublishing(verification)) return null;
  return verification === "unverified"
    ? "Upload your GS1 licence certificate"
    : "Send another document";
}
